import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import JobListItem from './JobListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import type { Job } from 'api/smartRecruiter/types'
import { getJobPostings } from 'api/smartRecruiter/smartRecruiter'
import CommunityBanner from './CommunityBanner'
import { formatIsoDate } from 'lib/datetime/dateUtils'

const JobListContainer = styled.div`
  width: 100%;
  border: none;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
  margin-top: ${rem(15)};
  border-radius: ${props => props.theme.borderRadius.S};
`

function OpenPositions() {
  const intl = useIntl()
  const [jobs, setJobs] = useState<Array<Job>>([])
  const [status, setStatus] = useState<'loading' | 'error' | 'no-jobs' | 'available'>('loading')

  useEffect(() => {
    const fetchJobs = async() => {
      setStatus('loading')
      try {
        const jobs = await getJobPostings()
        if (jobs.length === 0) {
          setStatus('no-jobs')
        } else {
          setJobs(jobs)
          setStatus('available')
        }
      } catch(err) {
        console.error('Failed to fetch jobs', err)
        setStatus('error')
      }
    }

    fetchJobs()
  }, [])

  if (status === 'loading') {
    return (
      <JobListContainer>
        <Group direction="vertical" horizontalAlign="center">
          <BodyText variant="small">{intl.formatMessage({ id: 'd+jcrnA1yD', defaultMessage: 'Loading jobs...', description: 'Loading indicator text while job postings are being fetched on the careers page' })}</BodyText>
        </Group>
      </JobListContainer>
    )
  }

  if (status === 'error') {
    return (
      <JobListContainer>
        <BodyText variant="small" style={{ color: 'red' }}>
          {intl.formatMessage({
            id: 'vj7/CYUawa',
            defaultMessage: 'Sorry, we couldn\u2019t load the job listings. Please try again later.',
            description: 'Error message when job postings fail to load on the careers page',
          })}
        </BodyText>
      </JobListContainer>
    )
  }

  if (status === 'no-jobs') {
    return (
      <JobListContainer>
        <BodyText variant="small">{intl.formatMessage({ id: 'hXEriSdLWq', defaultMessage: 'No job openings at the moment.', description: 'Empty state message when no job openings are available on the careers page' })}</BodyText>
      </JobListContainer>
    )
  }

  return (
    <JobListContainer>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={8}>
          {jobs.map(job => (
            <JobListItem
              key={job.id}
              title={job.title}
              location={job.location}
              employmentType={job.employmentType}
              date={formatIsoDate(new Date(job.date))}
              description={job.description}
              href={job.applyUrl}
              target="_blank"
            />
          ))}
        </Group>
        <CommunityBanner
          description={intl.formatMessage({
            id: 'PZJla/3Mvt',
            defaultMessage: 'If you are thinking about a future with us, sign up to the relevant talent community and be informed of any new opportunities.',
            description: 'Description for talent community sign-up banner on the careers page',
          })}
          title={intl.formatMessage({
            id: 'haceqfTPpE',
            defaultMessage: 'We\u2019re always on the lookout for talent',
            description: 'Title for talent community sign-up banner on the careers page',
          })}
        />
      </Group>
    </JobListContainer>
  )
}

OpenPositions.displayName = 'OpenPositions'
export default OpenPositions
